/**
 * Compact segmented theme control: Auto · Licht · Donker.
 *
 * Auto button shows the currently-resolved theme next to its label
 * ("Auto · Donker" or "Auto · Licht") so the operator can tell at a
 * glance which mode is actually painting.
 *
 * Two visual variants:
 *   - "ops":   white-on-dark, used inside the perma-dark ops sidebar.
 *   - "admin": uses sidebar-* tokens, adapts to the active theme.
 *
 * Accessible: real <button>s inside `role="radiogroup"`,
 * `aria-checked` on the selected segment, `aria-label` describing
 * the action, keyboard reachable.
 */
import { Monitor, Moon, Sun } from 'lucide-react';

const OPTIONS = [
  { value: 'auto', label: 'Auto', Icon: Monitor },
  { value: 'light', label: 'Licht', Icon: Sun },
  { value: 'dark', label: 'Donker', Icon: Moon },
];

export default function AdminThemeToggle({
  preference, effectiveTheme, onChange, variant = 'admin',
}) {
  const isOps = variant === 'ops';
  const wrapperCls = isOps
    ? 'flex items-stretch rounded-lg bg-white/5 p-0.5 ring-1 ring-white/10'
    : 'flex items-stretch rounded-lg bg-sidebar-accent/60 p-0.5 ring-1 ring-sidebar-border';
  const selectedCls = isOps
    ? 'bg-white/15 text-white shadow-sm'
    : 'bg-card text-foreground shadow-sm';
  const idleCls = isOps
    ? 'text-white/55 hover:text-white/90'
    : 'text-muted-foreground hover:text-foreground';

  return (
    <div role="radiogroup" aria-label="Themavoorkeur" className="space-y-1">
      <div className={wrapperCls} data-testid="admin-theme-toggle">
        {OPTIONS.map(({ value, label, Icon }) => {
          const selected = preference === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`Themavoorkeur: ${label}`}
              data-testid={`admin-theme-${value}`}
              onClick={() => onChange(value)}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selected ? selectedCls : idleCls
              }`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{label}</span>
            </button>
          );
        })}
      </div>
      {preference === 'auto' && (
        <p
          data-testid="admin-theme-auto-resolved"
          className={`text-[10px] leading-tight px-1 ${
            isOps ? 'text-white/40' : 'text-muted-foreground'
          }`}
        >
          Volgt systeem · {effectiveTheme === 'dark' ? 'Donker' : 'Licht'}
        </p>
      )}
    </div>
  );
}
